import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ImageToText = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-to-text',
			name: 'Image to Text Converter — Extract Text from Photo Online | Lift',
			description:
				'Convert image to text instantly with AI. No watermark. Upload photo, extract text from pictures, get prompts, descriptions, and summaries in seconds. Try now!',
			url: 'https://liftapp.ai/image-to-text',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-to-text#product' },
				{ '@id': 'https://liftapp.ai/image-to-text#howto' },
				{ '@id': 'https://liftapp.ai/image-to-text#benefits' },
				{ '@id': 'https://liftapp.ai/image-to-text#faq' },
				{ '@id': 'https://liftapp.ai/image-to-text#why-lift' },
				{ '@id': 'https://liftapp.ai/image-to-text#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-to-text#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-to-text#product',
			name: 'Image to Text Converter',
			description:
				"Extract text from any image in seconds with Lift's AI. Get prompts, detailed descriptions, object lists, and summaries, no watermarks.",
			url: 'https://liftapp.ai/image-to-text',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-to-text#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Image',
					text: 'Upload photos of documents, handwritten notes, product packaging, or any visual with text.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Pick an Option',
					text: 'Choose from 5 powerful options: Extract Text, Get Prompt, Describe Fully, Find Objects, or Summarize.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Get Your Result Instantly',
					text: 'Watch as AI scans and reads the image in seconds.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Copy, Edit, or Regenerate',
					text: 'Use the result right away — copy extracted text, edit the output to your needs, or regenerate for an alternative version.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-text#benefits',
			name: 'Who can benefit from Image to Text Converter?',
			description:
				"Lift's Image to Text Converter helps social media managers, e-commerce sellers, SEO marketers, students, and AI creators extract and use text from images instantly.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Social Media Managers — Instantly generate captions, alt texts, and image summaries from any visual.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'E-commerce Sellers — Extract text from product packaging and photos to speed up marketplace listings.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'SEO & Digital Marketers — Scale image optimization with AI-powered descriptions and alt tags.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Students & Professionals — Extract text from screenshots, scanned documents, and handwritten notes in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'AI Creators & Designers — Generate accurate prompts from reference images to recreate or refine visuals with AI tools.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift AI image to text converter online for creators, marketers, and sellers',
				description:
					'Lift AI image to text converter online for creators, marketers, and sellers',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-to-text#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'How does Lift Image to Text Converter work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift uses AI-powered OCR and image analysis to read and interpret your photo. Simply upload a photo, then choose an option — Extract Text, Get Prompt, Describe Fully, Find Objects, or Summarize. The AI scans the image in seconds and delivers the result, which you can copy, edit, or regenerate instantly.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can it recognize handwritten text?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes. Lift's AI image to text tool supports OCR for handwritten text, including notes, letters, and handwritten documents. Results may vary depending on handwriting clarity, but the tool handles most legible handwritten content accurately.",
					},
				},
				{
					'@type': 'Question',
					name: 'Does it support multiple languages?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The image to text converter can recognize and extract text from images in multiple languages. Simply upload your photo and the AI will detect and convert the text regardless of the language used in the image.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe — do you store my images?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes, completely safe. Your images are processed securely and automatically deleted from our servers immediately after compression. We never store, share, or access your files — your privacy is our priority.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-text#why-lift',
			name: "Why Lift's Image to Text Converter stands out",
			description:
				"Lift's Image to Text Converter combines high-quality AI results, intelligent rendering, cross-platform support, and affordability.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Image to Text Converter',
				description:
					'Lift AI image to text converter — fast, accurate online tool',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-to-text#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-to-text#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Image to Text Converter',
					item: 'https://liftapp.ai/image-to-text',
				},
			],
		},
	],
};
